// ariaScreenState.js — WHAT SHE CAN SEE (Braeden 8/16)
//
//   "she needs to be aware of whats happening everywhere, and reflext on that
//    ... it knows how to do anythign on the webstie, whats on the screen,
//    whats being typed ... she needs to know how to read everything and see
//    everything too"
//
// THE DISTINCTION THAT DRIVES THIS:
//   the search bar is a TOOL   — you type at it, it hands back information.
//   the speaker  is the ASSISTANT — it is in the room, it can see the room,
//                                   and it can work the tools for you.
//
// Until now she was neither. Every conversation turn shipped the words and
// NOTHING else: not which page was open, not what was half-typed in the box,
// not whether a website was up on screen. So "what am I looking at?" was
// unanswerable, and "open my leads" had nowhere to land. An assistant that
// cannot see the screen is a search bar with a microphone.
//
// This snapshots the app on every turn. It stays deliberately small — a few
// hundred bytes of FACTS, no DOM dumps and no page scrapes, because the point
// is for her to know where she is, not to bury the model in markup.

// ── SHE ACTUALLY READS THE SCREEN (Braeden 8/16) ─────────────────────────
//
//   "why can we read it, needs to be able to see the screen"
//
// The snapshot below told her WHICH page was open and what was typed. It did
// not contain a single word of what was on it — so "what does that say?",
// "read me the third one", "is the total right?" were all unanswerable, and
// "she can see the screen" was true only in the thinnest sense.
//
// This reads the DOM the way the desktop Assist reads the accessibility tree:
// structure, not pixels. Only VISIBLE elements, deduped, capped — a screen
// dump would cost more tokens than the conversation and bury the answer.
//
// Deliberately NOT a screenshot. Vision costs money per turn and would tell her
// less: text she can quote exactly beats an image she has to interpret.
function visible(el) {
  try {
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    if (r.bottom < 0 || r.top > window.innerHeight) return false;
    const cs = getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.display !== 'none' && Number(cs.opacity) > 0.05;
  } catch (_) { return false; }
}

const clean = (s, n = 90) => String(s || '').replace(/\s+/g, ' ').trim().slice(0, n);

export function readScreen(limit = 40) {
  if (typeof document === 'undefined') return null;
  const out = { headings: [], buttons: [], fields: [], text: [] };
  const seen = new Set();
  const push = (arr, v) => {
    if (!v) return;
    const k = arr === out.text ? 't:' + v : v;
    if (seen.has(k) || arr.length >= limit) return;
    seen.add(k); arr.push(v);
  };
  try {
    for (const el of document.querySelectorAll('h1,h2,h3,[role="heading"]')) {
      if (visible(el)) push(out.headings, clean(el.textContent, 70));
    }
    for (const el of document.querySelectorAll('button,[role="button"],a[href]')) {
      if (!visible(el)) continue;
      const label = clean(el.getAttribute('aria-label') || el.textContent, 40);
      if (label) push(out.buttons, label);
    }
    for (const el of document.querySelectorAll('input,textarea,select')) {
      if (!visible(el)) continue;
      const label = clean(el.getAttribute('aria-label') || el.placeholder || el.name, 40);
      const val = clean(el.value, 60);
      if (label || val) push(out.fields, val ? `${label || 'field'}: ${val}` : (label || ''));
    }
    // The prose. Leaf nodes only — taking every ancestor's text repeats the
    // whole page once per level.
    for (const el of document.querySelectorAll('p,li,td,span,div')) {
      if (out.text.length >= limit) break;
      if (el.children.length) continue;
      if (!visible(el)) continue;
      const t = clean(el.textContent, 110);
      if (t.length > 2) push(out.text, t);
    }
  } catch (_) {}
  return out;
}

/** What is actually on the screen right now. */
export function snapshot(o = {}) {
  const s = {
    // where they are
    section: o.section || null,
    // the tool: what is sitting in the search/ask box, typed but not sent.
    // She should know he is mid-sentence — it is the difference between
    // answering him and interrupting him.
    typed: (o.typed || '').slice(0, 200) || null,
    // what she is doing
    micOn: !!o.micOn,
    speaking: !!o.speaking,
    thinking: !!o.loading,
    // is she mid-script, and where
    onboarding: o.firstRunNode ? { step: o.firstRunNode, data: o.firstRunData || null } : null,
    // things that are literally displayed
    showing: [],
    // the last few turns, so "what did you just say" has an answer
    recent: (o.messages || []).slice(-4).map((m) => ({
      who: m.role === 'user' ? 'them' : 'her',
      text: String(m.content || '').slice(0, 160),
    })),
  };

  if (o.sitePreview && o.sitePreview.url) {
    s.showing.push({
      what: 'website',
      url: o.sitePreview.url,
      host: o.sitePreview.host || null,
      loading: !!o.sitePreview.loading,
    });
  }
  if (o.bizPlusOpen) s.showing.push({ what: 'business_setup' });
  if (o.screenShareOn) s.showing.push({ what: 'assist_mode' });

  // Viewport, because "make it bigger" and "I can't see it" depend on it.
  try {
    s.viewport = { w: window.innerWidth, h: window.innerHeight,
                   mobile: window.innerWidth < 768 };
  } catch (_) {}

  // WHAT IT ACTUALLY SAYS. Without this she knows the page name and nothing
  // written on it, which is not seeing the screen.
  if (o.read !== false) s.read = readScreen(o.readLimit || 40);

  return s;
}

// ── WHAT SHE IS ALLOWED TO DO TO THE APP ──────────────────────────────────
//
//   "the voice that im talking to needs to take control of the entire program
//    if i ask it, it knows how to do anythign on the webstie"
//
// An explicit vocabulary, not a free hand. Two reasons it is a list rather
// than "let the model drive": every one of these has to actually EXIST in the
// client (a made-up action is a silent no-op, and she would report success),
// and the destructive ones must be gated. Anything not on this list, she
// cannot do — and should say so instead of pretending.
export const UI_ACTIONS = {
  navigate:      { args: ['section'], safe: true,  what: 'open a page: ask, leads, jobs, books, systems, explore, imagegen, subscription' },
  show_site:     { args: ['url'],     safe: true,  what: 'pull a website up on screen' },
  close_panel:   { args: [],          safe: true,  what: 'close whatever is showing' },
  set_input:     { args: ['text'],    safe: true,  what: 'type something into the ask box for them' },
  mic:           { args: ['on'],      safe: true,  what: 'open or mute the microphone' },
  read_screen:   { args: [],          safe: true,  what: 'say what is currently on screen' },
  open_setup:    { args: [],          safe: true,  what: 'open the business setup box' },
  // SHE DOES IT, THEY DO NOT (Braeden 9/4): "thats when she does the whole new
  // project thing". The onboarding used to tell them to click three lines and
  // then click New project — that is the person doing the work, which is
  // backwards. With this she opens the drawer and the New-project box herself
  // and narrates it, which is also what the rest of the tour already does when
  // it types the title in for them.
  new_project:   { args: [],          safe: true,  what: 'open the new-project box yourself and start one' },
  open_account:  { args: [],          safe: true,  what: 'open the sign-up box so they can make an account (membership and the desktop app follow)' },
  point:         { args: ['at'],      safe: true,  what: 'point at something on screen (blue arrow) — at is a CSS selector or "text:<label>"; optional section navigates first' },
  start_assist:  { args: [],          safe: false, what: 'take control of the computer (needs a yes)' },
  // ASK BEFORE TAKING THE SCREEN (Braeden 8/22): "can i go into assist mode,
  // with a pop up, that says allow". This action does not DO anything - it
  // puts the Allow / Not now popup on screen. Nothing is opened and no
  // control is taken until he clicks Allow, which is why it is marked safe:
  // the unsafe part is start_assist, which the popup fires afterwards.
  assist_permission: { args: ['reason'], safe: true, what: 'ask permission to take control, with a popup' },
};

/** Is this something she can actually do, with the args it needs? */
export function validAction(a) {
  if (!a || typeof a !== 'object' || !a.type) return false;
  const spec = UI_ACTIONS[a.type];
  if (!spec) return false;
  for (const need of spec.args) {
    if (a[need] === undefined || a[need] === null || a[need] === '') return false;
  }
  return true;
}

/** A plain-English list of what she can do — for her prompt, not for the UI. */
export function capabilityList() {
  return Object.entries(UI_ACTIONS)
    .map(([k, v]) => `${k}(${v.args.join(',') || ''}) - ${v.what}`)
    .join('\n');
}
